import React, { useEffect, useState } from 'react';
import Heading from '../../../../common/element/Heading';
import Button from '../../../../common/element/Buttons';
import Dialog from '../../../../common/element/Dialog';
import BtnPrimary from '../../../../common/element/Buttons/btnPrimary';
import { setBreadcrumbs } from '../../../../redux/Admin/breadcrumbsSlice';
import { useDispatch, useSelector} from 'react-redux';
import { setEmployeeName, resetEmployeeName } from '../../../../redux/Admin/employeeViewSlice';

import SessionList from './Tabs/Employee/SessionView';
import Overview from './Tabs/Employee/OverViewEmployee';

import EmployeeView from './Tabs/Employee/EmployeeView';
import { useLocation, useNavigate } from 'react-router-dom';

import { clearCompanyEmployeeInfo, getCompanyEmployeeInfo, lockCompanyEmployee } from '../../../../redux/Employee/employeesSlice';
import { selectCompanyEmployeeInfo, selectCompanyEmployeeInfoLoading, selectEmployeeDetail, selectLockCompanyEmployeeLoading } from '../../../../redux/Employee/employeesSelector';
import SweetAlertService from '../../../../common/SweetAlert/SweetAlert';
import { CompanyAvatar } from '../../../../common/element/CompanyAvatar/CompanyAvatar';

// ✅ NEW: Import ImagePreviewModal
import ImagePreviewModal from '../../../../common/ImagePreviewModal/ImagePreviewModal';

const ViewEmployee = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  // ✅ Read URL params
  const searchParams = new URLSearchParams(location.search);

  const empID = searchParams.get('empId');

  const sessionView = searchParams.get('sessionView');
  const sessionId = searchParams.get('sessionId');
  const sessionName = searchParams.get('sessionName');
  const tabFromUrl = searchParams.get('tab') || 'employeeOverview';
  

  const companyEmployeeInfo = useSelector(selectCompanyEmployeeInfo);
  const lockLoading = useSelector(selectLockCompanyEmployeeLoading);
   const source = companyEmployeeInfo || {};

  const empCompany = source?.company_name || source?.companyName || source?.company || '';
  const empMobile = source?.employee_mobile_number || source?.employee_mobile || source?.mobile || source?.phone || '';
  const empEmail = source?.employee_email || source?.employeeEmail || source?.email || '';
  const isActive = source?.isActive ? 'Active' : 'Inactive'
  const empName = source?.employee_name || source?.employeeName || source?.name || source?.full_name || '-';
  const storedEmployeeName = useSelector((state) => state.employeeView?.employeeName);
  const storedSessionName = useSelector((state) => state.sessionView?.sessionName);

  // ✅ Set active tab from URL
  const [activeTab, setActiveTab] = useState(tabFromUrl);

  // ✅ NEW: State for the Image Preview Modal
  const [imageModal, setImageModal] = useState({
    isOpen: false,
    url: "",
    alt: "",
  });

  // ✅ Update state when URL changes
  useEffect(() => {
    setActiveTab(tabFromUrl);
  }, [tabFromUrl]);

  // ✅ Handle tab click + update URL
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    const params = new URLSearchParams(location.search);
    params.set('tab', tabId);

    params.delete('sessionView'); // remove sessionView when switching main tabs

    navigate(`?${params.toString()}`, { replace: true });
  };

  // ✅ Breadcrumbs — build crumbs from URL params so they work on refresh
  useEffect(() => {
    // prefer persisted name first so refresh shows the name immediately
    const employeeLabel = storedEmployeeName || empName || 'Employee View';
    const crumbs = [
      { label: "Employees", url: "/employee", home: "/companies" },
      { label: employeeLabel, url: `/employee/view?empId=${empID}&tab=${tabFromUrl}` },
    ];

    if (sessionView) {
      crumbs.push({
        label: storedSessionName || sessionName || "Session View",
        url: `/employee/view?sessionId=${sessionId}&tab=${tabFromUrl}&empId=${empID}&sessionView=${sessionView}`,
      });
    }

    if (empID) {
      dispatch(setBreadcrumbs(crumbs));
    }
  }, [dispatch, empID, tabFromUrl, sessionView, sessionId, storedEmployeeName, empName, storedSessionName, sessionName]);

 

  // ✅ Tabs with "Supported Documents"
  const tabs = [
    { id: 'employeeOverview', label: 'Employee Overview' },
    { id: 'sessions', label: 'Sessions' },
    // { id: 'assessments', label: 'Assessments' },
    // { id: 'assignment', label: 'Assignment' },
    // { id: 'supportedDocuments', label: 'Supported Documents' }, 
  ];

 

  useEffect(() => {


    dispatch(getCompanyEmployeeInfo({ companyEmployeeId: empID }));

  }, [empID, dispatch]);

  // persist employee name when info arrives
  useEffect(() => {
    const empNameFromSource = companyEmployeeInfo?.employee_name || companyEmployeeInfo?.employeeName || companyEmployeeInfo?.name || companyEmployeeInfo?.full_name;
    if (empNameFromSource) {
      dispatch(setEmployeeName(empNameFromSource));
    }
  }, [companyEmployeeInfo, dispatch]);

  // clear persisted employee name on unmount (but not on full page reload)
  useEffect(() => {
    const isReload = { current: false };
    const onBeforeUnload = () => { isReload.current = true; };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
      if (!isReload.current) dispatch(resetEmployeeName());
    };
  }, [dispatch]);

  // Dialog for lock/unlock
  const [dialogOpen, setDialogOpen] = useState(false);
  const handleDialogOpen = () => setDialogOpen(true);
  const handleDialogClose = () => setDialogOpen(false);

  const handleConfirmLock = async () => {
    const currentStatus = companyEmployeeInfo?.employee_status || companyEmployeeInfo?.status || '';
    const newStatus = currentStatus === 'unlock' ? 'lock' : 'unlock';
    try {
      const data = await dispatch(lockCompanyEmployee({ company_employee_id: Number(empID), status: newStatus })).unwrap();
      const successMessage = 'Employee status updated successfully';
      SweetAlertService.showSuccess({
        title: 'Success!',
        message: (data && data.message) || successMessage,
        onConfirm: () => {
          dispatch(getCompanyEmployeeInfo({ companyEmployeeId: empID }));
          handleDialogClose();
        }
      });
    } catch (err) {
      const errorMessage = err?.message || err?.data?.message || (err && err.toString()) || 'Something went wrong';
      SweetAlertService.showError({ title: 'Error!', message: errorMessage });
      console.error('Failed to toggle lock status', err);
    } finally {
      handleDialogClose();
    }
  };

  // ✅ NEW: Image Preview Handlers
  const openImagePreview = () => {
    if (!source?.profile_image) return;
    setImageModal({ 
      isOpen: true, 
      url: source.profile_image, 
      alt: empName || "Profile Image" 
    });
  };

  const closeImagePreview = () => {
    setImageModal({ isOpen: false, url: "", alt: "" });
  };

  return (
    <div>
      <div className="flex justify-between mb-5">
        <div className="w-full">
          <div className="flex items-center gap-4 mb-6">
            
            {/* ✅ NEW: Wrap CompanyAvatar in clickable div */}
            <div 
              onClick={openImagePreview}
              className={source?.profile_image ? "cursor-pointer hover:opacity-80 transition" : ""}
              title={source?.profile_image ? "View Profile Image" : ""}
            >
              <CompanyAvatar logo={source?.profile_image} name={empName} />
            </div>
            
            <div>
              <div className="w-80 h-10 text-start">
                {/* Prefer explicit fields from payload and provide safe fallbacks */}
                <Heading>{empName}</Heading>
              </div>

              <div className="flex items-center gap-3 mt-1">
                 <div className="flex items-center gap-3 mt-1">
                  <div className={`flex items-center gap-1 px-3 py-0.5 ${isActive === 'Active' ? 'bg-[#ECFDF3]' : 'bg-slate-100'} rounded-[16px]`}>
                    <div className={`w-2 h-2 ${isActive === 'Active' ? 'bg-green-500' : 'bg-red-500'} rounded-full`}></div>
                    <span className={`font-lato font-medium text-[14px] ${isActive === 'Active' ? 'text-[#027A48]' : 'text-[#slate-700]'}`}>
                      {isActive}
                    </span>
                  </div>
                    <span className="font-lato font-medium text-[14px] text-[#1D2939] flex items-center gap-2">

                  <span>{empEmail}</span> •  <span>{source.employee_mobile_number}</span>
                </span>

                </div>


                {/* show email if present */}

              </div>
            </div>
            {/* Lock/Unlock Button */}
            {(sessionView !== "view") && (
              <div className="ml-auto">
                <Button
                  onClick={handleDialogOpen}
                  className={`border flex items-center justify-center rounded-lg h-9 text-white text-sm font-semibold duration-150 gap-1 px-5 ${
                    (companyEmployeeInfo?.employee_status === 'lock')
                      ? 'bg-[#22C55E] border-[#22C55E] hover:bg-[#16A34A]'
                      : 'bg-[#FF8E1C] border-[#FF8E1C] hover:bg-[#e57d12]'
                  }`}
                >

                  {companyEmployeeInfo?.employee_status === 'unlock' ? 'Lock' : 'Unlock'}
                  {companyEmployeeInfo?.employee_status === 'lock' ? (
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M14.0625 8.00003H5.34375V4.77816C5.34375 3.54066 6.4582 2.51253 7.85039 2.50003C9.25664 2.48753 10.4062 3.50316 10.4062 4.75003V5.25003C10.4062 5.66566 10.7824 6.00003 11.25 6.00003H12.375C12.8426 6.00003 13.2188 5.66566 13.2188 5.25003V4.75003C13.2188 2.12503 10.8105 -0.00934423 7.85742 3.07662e-05C4.9043 0.00940577 2.53125 2.17191 2.53125 4.79691V8.00003H1.6875C0.755859 8.00003 0 8.67191 0 9.50003V14.5C0 15.3282 0.755859 16 1.6875 16H14.0625C14.9941 16 15.75 15.3282 15.75 14.5V9.50003C15.75 8.67191 14.9941 8.00003 14.0625 8.00003ZM9.28125 12.75C9.28125 13.4407 8.65195 14 7.875 14C7.09805 14 6.46875 13.4407 6.46875 12.75V11.25C6.46875 10.5594 7.09805 10 7.875 10C8.65195 10 9.28125 10.5594 9.28125 11.25V12.75Z" fill="white" />
                    </svg>
                  ) : (
                    <svg width="14" height="16" viewBox="0 0 14 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12.5 7H11.75V4.75C11.75 2.13125 9.61875 0 7 0C4.38125 0 2.25 2.13125 2.25 4.75V7H1.5C0.671875 7 0 7.67188 0 8.5V14.5C0 15.3281 0.671875 16 1.5 16H12.5C13.3281 16 14 15.3281 14 14.5V8.5C14 7.67188 13.3281 7 12.5 7ZM9.25 7H4.75V4.75C4.75 3.50938 5.75938 2.5 7 2.5C8.24063 2.5 9.25 3.50938 9.25 4.75V7Z" fill="white" />
                    </svg>
                  )}
                </Button>
              </div>
            )}
          </div>
          {/* ✅ Header Section (Hide in Employee Detail View) */}
          {(sessionView !== "view") && (
            <>


              {/* ✅ Tabs */}
              <div className="flex gap-0 p-1 bg-[#FFF4E5] border border-[#FFF4E5] rounded-lg mb-6">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => handleTabChange(tab.id)}
                    className={`flex-1 text-sm font-normal transition-colors py-2 px-3 rounded-md ${activeTab === tab.id
                      ? 'text-gray-900 bg-white shadow-[0px_1px_3px_rgba(16,24,40,0.1),0px_1px_2px_rgba(16,24,40,0.06)]'
                      : 'text-gray-600 bg-[#FFF4E5]'
                      }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </>
          )}

          {/* ✅ Tab Content */}

          {/* Confirmation Dialog */}
          <Dialog isOpen={dialogOpen} onClose={handleDialogClose} width={380}>
            <div className="flex flex-col items-center justify-center py-8 px-6">
              <div className="bg-[#FF8E1C] p-3 rounded-full mb-4">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14.0625 8.00003H5.34375V4.77816C5.34375 3.54066 6.4582 2.51253 7.85039 2.50003C9.25664 2.48753 10.4062 3.50316 10.4062 4.75003V5.25003C10.4062 5.66566 10.7824 6.00003 11.25 6.00003H12.375C12.8426 6.00003 13.2188 5.66566 13.2188 5.25003V4.75003C13.2188 2.12503 10.8105 -0.00934423 7.85742 3.07662e-05C4.9043 0.00940577 2.53125 2.17191 2.53125 4.79691V8.00003H1.6875C0.755859 8.00003 0 8.67191 0 9.50003V14.5C0 15.3282 0.755859 16 1.6875 16H14.0625C14.9941 16 15.75 15.3282 15.75 14.5V9.50003C15.75 8.67191 14.9941 8.00003 14.0625 8.00003ZM9.28125 12.75C9.28125 13.4407 8.65195 14 7.875 14C7.09805 14 6.46875 13.4407 6.46875 12.75V11.25C6.46875 10.5594 7.09805 10 7.875 10C8.65195 10 9.28125 10.5594 9.28125 11.25V12.75Z" fill="white" />
                </svg>

              </div>

              <h3 className="text-gray-900 text-sm font-medium mb-6 text-center">
                {companyEmployeeInfo?.employee_status === 'unlock'
                  ? 'Are you sure you want to Lock Employee?'
                  : 'Are you sure you want to Unlock Employee?'}
              </h3>

              <div className="flex justify-between gap-3 w-full">
                <button
                  onClick={handleDialogClose}
                  className="flex-1 border border-gray-200 rounded-lg h-10 bg-[#FFF8F3] text-gray-700 text-sm font-semibold hover:bg-orange-50"
                >
                  Cancel
                </button>
                <BtnPrimary
                  onClick={handleConfirmLock}
                  className="flex-1"
                  style={{ backgroundColor: '#FF8E1C', borderColor: '#FF8E1C' }}
                  disabled={lockLoading}
                >
                  {lockLoading ? 'Processing...' : 'Confirm'}
                </BtnPrimary>
              </div>
            </div>
          </Dialog>
          {activeTab === 'employeeOverview' && <Overview companyData={companyEmployeeInfo} />}

          {activeTab === 'sessions' && (
            sessionView === "view"
              ? <EmployeeView employeeId={empID} />
              : <SessionList  />
          )}

        </div>
      </div>

      {/* ✅ NEW: Render the Image Preview Modal */}
      <ImagePreviewModal
        isOpen={imageModal.isOpen}
        onClose={closeImagePreview}
        imageUrl={imageModal.url}
        altText={imageModal.alt}
      />
      
    </div>
  );
};

export default ViewEmployee;